import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, RefreshCw, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  cleanAuthCallbackUrl,
  clearPasswordRecoveryState,
  getActiveAuthSession,
  getPasswordResetRedirectTo,
  getRememberedPasswordResetEmail,
  isCodeVerifierMissingError,
  isPasswordRecoveryExpired,
  rememberPasswordResetEmail,
} from '../lib/authRecovery';
import { toast } from '../lib/feedbackToast';

const MotionDiv = motion.div;

const PasswordResetModal = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRequestNew, setShowRequestNew] = useState(false);

  const handleRequestNew = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    const email = user?.email || getRememberedPasswordResetEmail();
    
    if (!email) {
      toast.error('Email not found', { description: 'Please go back to the login page and try again.' });
      setLoading(false);
      return;
    }

    rememberPasswordResetEmail(email);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getPasswordResetRedirectTo(),
    });

    if (error) {
      toast.error('Failed to send link', { description: error.message });
    } else {
      toast.success('Fresh link sent!', { description: 'Please check your email and open the link in THIS browser.' });
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (isPasswordRecoveryExpired()) {
      clearPasswordRecoveryState();
      cleanAuthCallbackUrl();
      setShowRequestNew(true);
      toast.error('Reset link expired', {
        description: 'Password reset links expire after 5 minutes. Please request a fresh link.',
        duration: 6000
      });
      return;
    }

    setLoading(true);

    const { session, error: sessionError } = await getActiveAuthSession();
    
    if (!session) {
      if (isCodeVerifierMissingError(sessionError)) {
        toast.error('Security Mismatch Detected', {
          description: 'Open a fresh reset link from this browser before changing your password.',
          duration: 6000
        });
        setShowRequestNew(true);
        setLoading(false);
        return;
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Security Token Invalid', {
          description: userError?.message || 'The security handshake failed. Your link may be expired. Please request a new one.'
        });
        setShowRequestNew(true);
        setLoading(false);
        return;
      }
    }

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast.error(error.message);
      setLoading(false);
    } else {
      toast.success('Password updated successfully!', {
        description: 'Your account is now secured with your new password.'
      });
      clearPasswordRecoveryState({ clearEmail: true });
      setLoading(false);
      onClose();
      // Clean up the URL one last time
      cleanAuthCallbackUrl();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[#050505]/80 backdrop-blur-md"
          />

          {/* Modal */}
          <MotionDiv
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-[2.5rem] shadow-[0_0_100px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Header Graphics */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-indigo-500/10 to-transparent pointer-events-none" />
            
            <div className="p-8 relative">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-indigo-500/10 rounded-[1.5rem] flex items-center justify-center text-indigo-500 border border-indigo-500/20">
                  <ShieldCheck size={32} />
                </div>
              </div>

              <div className="text-center space-y-2 mb-8">
                <h2 className="text-2xl font-black text-white tracking-tighter">Secure Password Reset</h2>
                <p className="text-slate-400 text-sm font-medium">Create a strong, unique password for your account.</p>
              </div>

              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-2">New Password</label>
                  <div className="relative">
                    <input 
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      style={{ WebkitBoxShadow: '0 0 0px 1000px #f8fafc inset', WebkitTextFillColor: '#0f172a' }}
                      className="password-reset-input w-full p-4 bg-slate-50 border border-transparent rounded-2xl text-slate-900 placeholder:text-slate-400 caret-indigo-500 outline-none focus:border-indigo-500 focus:bg-white transition-all font-bold"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-2">Confirm New Password</label>
                  <input 
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    style={{ WebkitBoxShadow: '0 0 0px 1000px #f8fafc inset', WebkitTextFillColor: '#0f172a' }}
                    className="password-reset-input w-full p-4 bg-slate-50 border border-transparent rounded-2xl text-slate-900 placeholder:text-slate-400 caret-indigo-500 outline-none focus:border-indigo-500 focus:bg-white transition-all font-bold"
                  />
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-white text-black rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-200 transition-all flex items-center justify-center gap-3 mt-4"
                >
                  {loading ? <RefreshCw className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                  {loading ? 'Securing Account...' : 'Update Password'}
                </button>
                
                {showRequestNew && (
                  <button 
                    type="button"
                    onClick={handleRequestNew}
                    disabled={loading}
                    className="w-full py-4 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-indigo-500/20 transition-all flex items-center justify-center gap-3 mt-2"
                  >
                    <RefreshCw className={loading ? "animate-spin" : ""} size={14} />
                    Send Fresh Link to this Browser
                  </button>
                )}
              </form>

              <button 
                onClick={() => {
                  clearPasswordRecoveryState();
                  onClose();
                }}
                className="w-full py-4 bg-transparent text-slate-500 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:text-white transition-all mt-4"
              >
                Cancel & Go Back
              </button>
            </div>
          </MotionDiv>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PasswordResetModal;
